import React, { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { filterCategoryTypesAtom } from '@src/atoms/page';
import { BaseChart, Retry } from '@components/common';
import { buildStackedOption, buildResponseTreeOption } from './utils';
import { useChartInstance } from '@src/components/common/BaseChart';
import { ChartWrapper } from '../styled';
import useResponseGroupBy from './hooks/useResponseGroupBy';
import useResponseTimeseries, { AVERAGE_REQUEST_COUNT_METRIC } from './hooks/useResponseTimeseries';
import { NETWORK_BASED_CATEGORY_EXCLUDES } from '@src/utils/presets/constants';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import Extra from './components/Extra';
import { StackWrapper } from './styled';
import { useWidget } from '../../state/dashboard';
import { useValidateParams } from './hooks/useValidateParams';
import WidgetWarning from '../../common/WidgetWarning';

function NetworkResponseWidget({ wid }) {
  const widgetHandler = useWidget({ wid });
  const [showTreeMap, setShowTreeMap] = useState(false);
  const filterCategories = useAtomValue(filterCategoryTypesAtom);

  const isNAFilter = filterCategories.some((category: any) => {
    return NETWORK_BASED_CATEGORY_EXCLUDES.includes(category);
  });

  const getWidgets = () => {
    if (isNAFilter) {
      return <WidgetWarning message="Not Applicable" />;
    } else if (!isIntervalValid) {
      return <WidgetWarning message={message} />;
    } else {
      return <StackedResponse wid={wid} showTreeMap={showTreeMap} />;
    }
  };

  const { isIntervalValid, message } = useValidateParams({});

  return (
    <WidgetCardWrapper
      wid={wid}
      title="Network: Response Code"
      style={{ gridRow: 'span 14' }}
      extra={
        <Extra
          showTreeMap={showTreeMap}
          setShowTreeMap={setShowTreeMap}
          widgetHandler={widgetHandler}
          showOptions={isIntervalValid && !isNAFilter}
        />
      }
    >
      <StackWrapper>{getWidgets()}</StackWrapper>
    </WidgetCardWrapper>
  );
}

function StackedResponse({ wid, showTreeMap }) {
  const { instance, chartInstanceRefCb } = useChartInstance({});

  const {
    isLoading: isGroupByLoading,
    filterList,
    refetch,
    errorTreeData,
  }: any = useResponseGroupBy({
    wid,
    metric: AVERAGE_REQUEST_COUNT_METRIC,
  });

  const {
    isLoading: isStackedTimeseriesLoading,
    data: chartData,
    queued,
  } = useResponseTimeseries({
    wid,
    filters: filterList,
    metrics: [AVERAGE_REQUEST_COUNT_METRIC],
    enabled: !isGroupByLoading && !!filterList.length,
  });

  const loading = isGroupByLoading || isStackedTimeseriesLoading;

  useEffect(() => {
    if (instance !== null && !loading) {
      let option: any = {};
      if (showTreeMap) {
        option = buildResponseTreeOption(errorTreeData);
      } else {
        option = buildStackedOption(chartData);
      }

      instance.setOption(option, { notMerge: true });
    }
  }, [instance, chartData, errorTreeData, loading, showTreeMap]);

  return (
    <ChartWrapper>
      <Retry fetchData={refetch} isLoading={loading} progress={true} queued={queued}>
        <BaseChart
          key="network-code-chart"
          chartId="network-code-chart"
          chartInstanceRefCb={chartInstanceRefCb}
          style={{ height: '100%', minHeight: 210 }}
        />
      </Retry>
    </ChartWrapper>
  );
}

export default NetworkResponseWidget;
